'use client';

import * as React from 'react';
import type { EdgeConfig, EdgeRawConfig } from '@/edge/types';
import { useIsomorphicLayoutEffect } from '@/shared/hooks/use-isomorphic-layout-effect';
import { normalizeConfig } from '../../utils/edge-config-normalization';
interface EdgeConfigProviderProps extends React.PropsWithChildren {
  edgeConfig: EdgeConfig;
  shouldConfigBePassedToGlobal?: boolean;
}
const EMPTY_EDGE_RAW_CONFIG = ({} as EdgeRawConfig);
export const ConfigContext = React.createContext<EdgeConfig>(normalizeConfig(EMPTY_EDGE_RAW_CONFIG));

/**
 * @useClient
 */
export const EdgeConfigProvider = React.memo(function EdgeConfigProvider({
  children,
  edgeConfig,
  shouldConfigBePassedToGlobal = true
}: EdgeConfigProviderProps) {
  const stringifiedEdgeConfig = JSON.stringify(edgeConfig ?? normalizeConfig(EMPTY_EDGE_RAW_CONFIG));
  const stableEdgeConfig = React.useMemo<EdgeConfig>(() => JSON.parse(stringifiedEdgeConfig), [stringifiedEdgeConfig]);
  React.useEffect(() => {}, [edgeConfig]);
  useIsomorphicLayoutEffect(() => {
    const config = JSON.parse(stringifiedEdgeConfig);
    if (!config.hasOwnProperty('scenes') || !config.hasOwnProperty('widgets')) {
      console.error('[EdgeConfigProvider] received empty edgeConfig');
    }

    /**
     * @note
     * We can fetch `edge config` multiple times, so we need to ensure that we are not overwriting the global
     * `window.config` object if it already exists.
     * We can also get `edge config` for different scenes at different levels of the component tree, so we need to
     * separate them in the object "by scenes" to have complete information about the current configuration.
     */
    if (shouldConfigBePassedToGlobal === true) {
      window.config = {
        ...window.config,
        ...(Array.isArray(config.scenes) ? {
          [config.scenes.join(' -> ')]: JSON.parse(stringifiedEdgeConfig)
        } : {})
      };
    }
  }, [shouldConfigBePassedToGlobal, stringifiedEdgeConfig]);
  return <ConfigContext.Provider value={stableEdgeConfig}>{children}</ConfigContext.Provider>;
});